import React, { useState, useRef, useEffect, useContext } from "react";
import { apiUrl } from "../config/api.js";
import { AuthContext } from "../contexts/AuthContext.js";
import "./styles/ChatWidget.css";

const GREETING = {
  role: "assistant",
  content:
    "Hi, I'm Vyra 👋 your VyralNews assistant. Ask me about today's headlines, a topic you want explained, or where to find stories on the site.",
};

const ChatWidget = () => {
  const { token } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    try {
      const payload = nextMessages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(1); // drop the local greeting

      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(apiUrl("/api/chat"), {
        method: "POST",
        headers,
        body: JSON.stringify({ messages: payload }),
      });

      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply || "Sorry, I couldn't respond right now." },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="vyra-chat">
      {open && (
        <div className="vyra-panel" role="dialog" aria-label="VyralNews chat assistant">
          <div className="vyra-header">
            <div className="vyra-header-info">
              <span className="vyra-avatar" aria-hidden="true">V</span>
              <div className="vyra-header-text">
                <span className="vyra-title">Vyra</span>
                <span className="vyra-subtitle">VyralNews Assistant</span>
              </div>
            </div>
            <button
              className="vyra-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="vyra-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`vyra-msg vyra-msg-${m.role}`}>
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="vyra-msg vyra-msg-assistant vyra-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>

          <div className="vyra-input-row">
            <textarea
              ref={inputRef}
              className="vyra-input"
              placeholder="Ask Vyra about the news..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <button
              className="vyra-send"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <button
        className={`vyra-bubble ${open ? "vyra-bubble-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat assistant" : "Open chat assistant"}
      >
        {open ? (
          "✕"
        ) : (
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
            <path
              d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.6-.8L3 21l1.8-5.4A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export default ChatWidget;
